/**
 * Mutations for per-conversion public/private toggling and user thumbnails
 * (#932). Both backend writes are 202-async (celery), so each mutation
 * resolves only once /data reflects the change — the component's isLoading
 * is an honest "still applying" signal, and a timeout rejects so the UI can
 * roll back.
 */
import { useMemo } from 'react';
import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';
import { FetchError } from 'src/types/TApiQueries';
import { Asset } from 'src/types/TAssets';
import { useAccessToken } from 'src/hooks/useAccessToken';
import {
  getAssetDetails,
  setConversionPublicAccess,
  uploadAssetThumbnail,
  setAssetThumbnailFromStoryboard,
  deleteAssetThumbnail,
} from 'src/api/functions/assetsApi';
import { queryFnFetchErrorWrapper } from '../utils/errorHandlers';
import { queryNames } from 'src/api/utils/queryNames';

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 30000;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Polls /data until `predicate(asset)` is true; resolves with the fresh
 *  asset payload or rejects on timeout. */
async function pollAssetUntil(
  assetId: string,
  accessToken: string,
  predicate: (asset: Asset) => boolean,
): Promise<Asset> {
  const deadline = Date.now() + POLL_TIMEOUT_MS;
  // first check is delayed one interval — the celery task needs a moment
  while (Date.now() < deadline) {
    await sleep(POLL_INTERVAL_MS);
    // getAssetDetails resolves to the parsed asset payload on success, or an
    // error object with a NUMERIC status on failure (the asset's own status
    // field is a string like 'OK')
    const result: any = await getAssetDetails(assetId, accessToken);
    if (result && typeof result.status !== 'number' && result.id) {
      const asset = result as Asset;
      if (predicate(asset)) return asset;
    }
  }
  throw new Error('timed out waiting for the change to apply');
}

/**
 * The custom-thumbnail version /data reports now (0 when there is none).
 * Read before a thumbnail write so the poll waits for a NEWER version:
 * `user_provided` alone is already true when one custom thumbnail replaces
 * another, and the poll used to "succeed" before the new image had landed.
 */
async function currentThumbnailVersion(assetId: string, accessToken: string): Promise<number> {
  const result: any = await getAssetDetails(assetId, accessToken);
  return Number(result?.addons_info?.ON_DEMAND_IMAGE?.version) || 0;
}

export const hasNewerCustomThumbnail = (before: number) => (asset: Asset) =>
  asset.addons_info?.ON_DEMAND_IMAGE?.user_provided === true &&
  (Number(asset.addons_info?.ON_DEMAND_IMAGE?.version) || 0) > before;

export type PublicAccessVariables = {
  conversionKey: string;
  isPublic: boolean;
};

export function useConversionPublicAccessMutation(
  assetId: string,
  mutationOptions?: UseMutationOptions<Asset, FetchError | Error, PublicAccessVariables>,
) {
  const queryClient = useQueryClient();
  const accessToken = useAccessToken();

  const mutationFn = useMemo(
    () => async ({ conversionKey, isPublic }: PublicAccessVariables) => {
      const request = queryFnFetchErrorWrapper(() =>
        setConversionPublicAccess(assetId, [conversionKey], isPublic, accessToken),
      );
      await request();
      return pollAssetUntil(
        assetId,
        accessToken,
        (asset) => asset.conversions?.[conversionKey]?.public === isPublic,
      );
    },
    [assetId, accessToken],
  );

  return useMutation(mutationFn, {
    ...mutationOptions,
    onSuccess: async (asset, ...args) => {
      // the poll already fetched the converged payload — publish it
      queryClient.setQueryData([queryNames.asset, assetId], asset);
      mutationOptions?.onSuccess?.(asset, ...args);
    },
    onError: async (error, ...args) => {
      // state is uncertain (queued but not observed) — refetch to resync
      await queryClient.invalidateQueries([queryNames.asset, assetId]);
      mutationOptions?.onError?.(error, ...args);
    },
  });
}

export function useAssetThumbnailMutation(
  assetId: string,
  mutationOptions?: UseMutationOptions<Asset, FetchError | Error, File>,
) {
  const queryClient = useQueryClient();
  const accessToken = useAccessToken();

  const mutationFn = useMemo(
    () => async (file: File) => {
      const before = await currentThumbnailVersion(assetId, accessToken);
      const request = queryFnFetchErrorWrapper(() =>
        uploadAssetThumbnail(assetId, file, accessToken),
      );
      await request();
      // fs_smart is surfaced under addons_info.ON_DEMAND_IMAGE, not conversions
      return pollAssetUntil(assetId, accessToken, hasNewerCustomThumbnail(before));
    },
    [assetId, accessToken],
  );

  return useMutation(mutationFn, {
    ...mutationOptions,
    onSuccess: async (asset, ...args) => {
      queryClient.setQueryData([queryNames.asset, assetId], asset);
      // listing thumbnails render from the same ODI URL — refresh the grid
      await queryClient.invalidateQueries([queryNames.search]);
      mutationOptions?.onSuccess?.(asset, ...args);
    },
    onError: async (error, ...args) => {
      await queryClient.invalidateQueries([queryNames.asset, assetId]);
      mutationOptions?.onError?.(error, ...args);
    },
  });
}

/** Use storyboard frame N (1-based) as the asset's thumbnail. */
export function useAssetThumbnailFromStoryboardMutation(
  assetId: string,
  mutationOptions?: UseMutationOptions<Asset, FetchError | Error, number>,
) {
  const queryClient = useQueryClient();
  const accessToken = useAccessToken();

  const mutationFn = useMemo(
    () => async (frame: number) => {
      const before = await currentThumbnailVersion(assetId, accessToken);
      const request = queryFnFetchErrorWrapper(() =>
        setAssetThumbnailFromStoryboard(assetId, frame, accessToken),
      );
      await request();
      return pollAssetUntil(assetId, accessToken, hasNewerCustomThumbnail(before));
    },
    [assetId, accessToken],
  );

  return useMutation(mutationFn, {
    ...mutationOptions,
    onSuccess: async (asset, ...args) => {
      queryClient.setQueryData([queryNames.asset, assetId], asset);
      // listing thumbnails render from the same ODI URL — refresh the grid
      await queryClient.invalidateQueries([queryNames.search]);
      mutationOptions?.onSuccess?.(asset, ...args);
    },
    onError: async (error, ...args) => {
      await queryClient.invalidateQueries([queryNames.asset, assetId]);
      mutationOptions?.onError?.(error, ...args);
    },
  });
}

export function useAssetThumbnailRevertMutation(
  assetId: string,
  mutationOptions?: UseMutationOptions<Asset, FetchError | Error, void>,
) {
  const queryClient = useQueryClient();
  const accessToken = useAccessToken();

  const mutationFn = useMemo(
    () => async () => {
      const request = queryFnFetchErrorWrapper(() => deleteAssetThumbnail(assetId, accessToken));
      await request();
      return pollAssetUntil(
        assetId,
        accessToken,
        (asset) => !asset.addons_info?.ON_DEMAND_IMAGE?.user_provided,
      );
    },
    [assetId, accessToken],
  );

  return useMutation(mutationFn, {
    ...mutationOptions,
    onSuccess: async (asset, ...args) => {
      queryClient.setQueryData([queryNames.asset, assetId], asset);
      await queryClient.invalidateQueries([queryNames.search]);
      mutationOptions?.onSuccess?.(asset, ...args);
    },
    onError: async (error, ...args) => {
      await queryClient.invalidateQueries([queryNames.asset, assetId]);
      mutationOptions?.onError?.(error, ...args);
    },
  });
}
